// ============================================================
// ExpressCrudWithMongoose.jsx
//
// A branded, responsive course content page for the
// "CRUD Operations with Mongoose" lesson.
//
// Author: ProDev Linux Series (Database Branch)
// Features: Full lifecycle of Mongoose operations, async/await
//           patterns, and navigation to the Express Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import StorageIcon           from "@mui/icons-material/Storage";
import AddCircleIcon         from "@mui/icons-material/AddCircle";
import VisibilityIcon        from "@mui/icons-material/Visibility";
import EditIcon              from "@mui/icons-material/Edit";
import DeleteForeverIcon     from "@mui/icons-material/DeleteForever";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import SchemaIcon            from "@mui/icons-material/Schema";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressCrudWithMongoose() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Data Persistence · Module 08"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Mongoose <span>CRUD Operations</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        CRUD forms the backbone of any data-driven application. Using Mongoose, we map our application objects to MongoDB documents, allowing for clean and readable database interactions.
                    </Typography>
                </Box>

                {/* 1. SCHEMA SETUP */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SchemaIcon />} label="1. Defining the Schema" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        A Schema defines the structure of the document, default values, and validation.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const mongoose = require('mongoose');", comment: "Import Mongoose" },
                        { cmd: "const userSchema = new mongoose.Schema({", comment: "" },
                        { cmd: "  name: String,", comment: "" },
                        { cmd: "  email: { type: String, unique: true, required: true },", comment: "" },
                        { cmd: "  age: Number", comment: "" },
                        { cmd: "});", comment: "" },
                        { cmd: "const User = mongoose.model('User', userSchema);", comment: "Exported Model" },
                    ]} />
                </Box>

                {/* 2. CRUD IMPLEMENTATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="2. CRUD Route Handlers" />

                    <Typography sx={{ color: BRAND.white, fontSize: 14, mb: 1, fontWeight: 700 }}>🟢 CREATE (POST)</Typography>
                    <CodeBlock lines={[
                        { cmd: "app.post('/users', async (req, res) => {", comment: "" },
                        { cmd: "  const newUser = new User(req.body);", comment: "Create instance from req.body" },
                        { cmd: "  const saved = await newUser.save();", comment: "" },
                        { cmd: "  res.status(201).json(saved);", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontSize: 14, mb: 1, mt: 3, fontWeight: 700 }}>🔵 READ (GET ALL)</Typography>
                    <CodeBlock lines={[
                        { cmd: "app.get('/users', async (req, res) => {", comment: "" },
                        { cmd: "  const users = await User.find();", comment: "Fetch everything" },
                        { cmd: "  res.json(users);", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontSize: 14, mb: 1, mt: 3, fontWeight: 700 }}>🟡 UPDATE (PUT)</Typography>
                    <CodeBlock lines={[
                        { cmd: "app.put('/users/:id', async (req, res) => {", comment: "" },
                        { cmd: "  const updated = await User.findByIdAndUpdate(req.params.id, req.body, { new: true });", comment: "{ new: true } returns updated doc" },
                        { cmd: "  res.json(updated);", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontSize: 14, mb: 1, mt: 3, fontWeight: 700 }}>🔴 DELETE (DELETE)</Typography>
                    <CodeBlock lines={[
                        { cmd: "app.delete('/users/:id', async (req, res) => {", comment: "" },
                        { cmd: "  await User.findByIdAndDelete(req.params.id);", comment: "" },
                        { cmd: "  res.json({ message: 'User Deleted' });", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* SUMMARY GRID */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StorageIcon />} label="CRUD Quick Map" />
                    <Grid container spacing={2}>
                        {[
                            { action: "CREATE", method: "POST", mng: ".save()", icon: <AddCircleIcon color="success" /> },
                            { action: "READ", method: "GET", mng: ".find()", icon: <VisibilityIcon color="info" /> },
                            { action: "UPDATE", method: "PUT", mng: ".findByIdAndUpdate()", icon: <EditIcon color="warning" /> },
                            { action: "DELETE", method: "DELETE", mng: ".findByIdAndDelete()", icon: <DeleteForeverIcon color="error" /> },
                        ].map((item) => (
                            <Grid item xs={12} sm={6} md={3} key={item.action}>
                                <Paper sx={{ p: 2, background: BRAND.surface, border: `1px solid ${BRAND.border}`, textAlign: 'center', borderRadius: 2 }}>
                                    {item.icon}
                                    <Typography sx={{ color: BRAND.white, fontWeight: 800, mt: 1 }}>{item.action}</Typography>
                                    <Typography sx={{ color: BRAND.gold, fontSize: 12, fontFamily: 'monospace' }}>{item.method}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{item.mng}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* MODULE OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Learning Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Design flexible MongoDB Schemas using Mongoose",
                            "Implement persistent Create operations with error handling",
                            "Retrieve and filter collection data using .find()",
                            "Perform targeted Updates using Document IDs",
                            "Safely remove records with atomic Delete operations",
                            "Utilize async/await for non-blocking database queries",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Always wrap your Mongoose calls in <code style={{ color: BRAND.goldBright }}>try/catch</code> blocks. Database operations can fail for many reasons (connection issues, validation errors, duplicate keys), and your API needs to respond gracefully.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
